import type { ApplicantCreation } from 'cpool-schemas';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddIncome } from './AddIncome';
import { IncomeItem } from './IncomeItem';
import { useApplicantAttribution } from '../../util/hooks/useApplicantAttribution';
import {
    getBaseIncomeDefaultValues,
    getOtherIncomeDefaultValues,
    getSecondaryEmploymentDefaultValues,
} from '../../util/values/income';
import {
    incomeListOrder,
    type IncomeType,
} from '~/features/leadDetails/components/Overview/CustomerData/Income/incomeTypeConfig.ts';
import { SectionHeading } from '~/features/leadDetails/components/Overview/CustomerData/shared/SectionHeading.tsx';

export const Income: React.FC = () => {
    const { t } = useTranslation();
    const { control } = useFormContext<ApplicantCreation>();
    const attribution = useApplicantAttribution();

    const sonstigeEinnahmen = useFieldArray({ control, name: 'einnahmen.sonstigeEinnahmen' });
    const unbefristeteZusatzrenten = useFieldArray({ control, name: 'einnahmen.unbefristeteZusatzrenten' });
    const ehegattenunterhalt = useFieldArray({ control, name: 'einnahmen.ehegattenunterhalt' });
    const einkuenfteAusKapitalvermoegen = useFieldArray({ control, name: 'einnahmen.einkuenfteAusKapitalvermoegen' });
    const einkuenfteAusNebentaetigkeit = useFieldArray({ control, name: 'einnahmen.einkuenfteAusNebentaetigkeit' });

    const fieldArrays = {
        ehegattenunterhalt,
        einkuenfteAusKapitalvermoegen,
        einkuenfteAusNebentaetigkeit,
        sonstigeEinnahmen,
        unbefristeteZusatzrenten,
    };

    const addIncome = (type: IncomeType) => {
        if (type === 'einkuenfteAusNebentaetigkeit') {
            einkuenfteAusNebentaetigkeit.append(getSecondaryEmploymentDefaultValues(attribution));
        } else if (type === 'sonstigeEinnahmen') {
            sonstigeEinnahmen.append(getOtherIncomeDefaultValues(attribution));
        } else {
            fieldArrays[type].append(getBaseIncomeDefaultValues(attribution));
        }
    };

    return (
        <section className="space-b-2 rounded bg-white pb-4">
            <SectionHeading action={<AddIncome onAdd={addIncome} />}>{t('LEADS.DETAILS.INCOME.INCOME')}</SectionHeading>
            <div className="flex flex-col gap-2">
                {incomeListOrder.flatMap((type) =>
                    fieldArrays[type].fields.map((field, index) => (
                        <IncomeItem
                            key={field.id}
                            index={index}
                            type={type}
                            onRemove={() => fieldArrays[type].remove(index)}
                        />
                    )),
                )}
            </div>
        </section>
    );
};
